import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Camera,
  Image,
  Loader2,
  X,
  Send,
} from 'lucide-react';
import {
  useCompanyDocuments,
  useUploadCompanyDocument,
  useSubmitCompanyRegistration,
} from '../../../hooks/useCompanyRegistration';

interface DocConfig {
  type: string;
  label: string;
  required: boolean;
  accept: string;
  capture?: string;
  icon: React.ElementType;
  description: string;
}

const DOCUMENT_CONFIGS: DocConfig[] = [
  { type: 'SOCIAL_CONTRACT', label: 'Contrato Social', required: true, accept: '.pdf', icon: FileText, description: 'PDF do contrato social da empresa' },
  { type: 'CNPJ_CARD', label: 'Cartão CNPJ', required: true, accept: '.pdf,.jpg,.jpeg,.png', icon: FileText, description: 'Comprovante de inscrição no CNPJ' },
  { type: 'DOC_FRONT', label: 'Documento — Frente', required: true, accept: '.jpg,.jpeg,.png', icon: Image, description: 'Frente do documento do representante (RG ou CNH)' },
  { type: 'DOC_BACK', label: 'Documento — Verso', required: true, accept: '.jpg,.jpeg,.png', icon: Image, description: 'Verso do documento do representante' },
  { type: 'SELFIE_WITH_DOC', label: 'Selfie com Documento', required: true, accept: '.jpg,.jpeg,.png', capture: 'user', icon: Camera, description: 'Foto segurando o documento ao lado do rosto' },
  { type: 'DOC_PDF', label: 'PDF do Documento', required: false, accept: '.pdf', icon: FileText, description: 'Versão digital do documento (opcional)' },
];

export function CompanyDocuments() {
  const navigate = useNavigate();
  const { data: documents, isLoading } = useCompanyDocuments();
  const uploadMutation = useUploadCompanyDocument();
  const submitMutation = useSubmitCompanyRegistration();
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const getDocForType = (type: string) =>
    documents?.find(d => d.documentType === type && !d.isReplaced);

  const requiredDocs = DOCUMENT_CONFIGS.filter(d => d.required);
  const uploadedRequired = requiredDocs.filter(d => getDocForType(d.type)).length;
  const allRequiredUploaded = uploadedRequired === requiredDocs.length;
  const progressPercent = (uploadedRequired / requiredDocs.length) * 100;

  const handleUpload = useCallback((file: File, docType: string) => {
    setUploadError(null);
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Arquivo excede o tamanho máximo de 10MB.');
      return;
    }
    const config = DOCUMENT_CONFIGS.find(d => d.type === docType);
    if (config) {
      const exts = config.accept.split(',').map(e => e.trim().replace('.', ''));
      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      if (!exts.some(e => e === fileExt || (e === 'jpeg' && fileExt === 'jpg') || (e === 'jpg' && fileExt === 'jpeg'))) {
        setUploadError(`Tipo de arquivo não aceito. Formatos aceitos: ${config.accept}`);
        return;
      }
    }
    uploadMutation.mutate({ file, documentType: docType });
  }, [uploadMutation]);

  const handleDrop = useCallback((e: React.DragEvent, docType: string) => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file, docType);
  }, [handleUpload]);

  const handleSubmit = () => {
    submitMutation.mutate(undefined, {
      onSuccess: () => navigate('/banking/company/status'),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white">Documentos da Empresa</h1>
        <p className="text-surface-400 mt-1">
          Envie os documentos obrigatórios para análise do seu cadastro.
        </p>
      </div>

      {/* Progress Bar */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-surface-300">
              {uploadedRequired} de {requiredDocs.length} documentos obrigatórios enviados
            </span>
            {allRequiredUploaded && (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <CheckCircle size={14} /> Todos enviados
              </span>
            )}
          </div>
          <div className="w-full bg-surface-800 rounded-full h-2">
            <div className="bg-primary-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      </Card>

      {/* Error */}
      {(uploadError || uploadMutation.isError || submitMutation.isError) && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-medium">Erro</p>
            <p className="text-red-300 text-sm">
              {uploadError || (uploadMutation.error as any)?.response?.data?.message
                || (submitMutation.error as any)?.response?.data?.message || 'Tente novamente.'}
            </p>
          </div>
          <button onClick={() => setUploadError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Document Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DOCUMENT_CONFIGS.map(config => {
          const doc = getDocForType(config.type);
          const isUploading = uploadMutation.isPending && uploadMutation.variables?.documentType === config.type;
          const isDragTarget = dragOver === config.type;

          return (
            <Card key={config.type}>
              <div
                className={`p-4 transition-colors ${isDragTarget ? 'bg-primary-500/10 border-primary-500' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(config.type); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={e => handleDrop(e, config.type)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <config.icon size={20} className={doc ? 'text-green-400' : 'text-surface-400'} />
                    <div>
                      <h3 className="text-sm font-medium text-white">{config.label}</h3>
                      <p className="text-xs text-surface-500">
                        {config.required ? 'Obrigatório' : 'Opcional'}
                      </p>
                    </div>
                  </div>
                  {doc ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">
                      Enviado
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400">
                      Pendente
                    </span>
                  )}
                </div>

                <p className="text-xs text-surface-400 mb-3">{config.description}</p>

                {/* Preview */}
                {doc && (
                  <div className="mb-3 p-2 rounded bg-surface-800 flex items-center gap-2">
                    {doc.fileMimeType?.startsWith('image/') ? (
                      <div className="w-12 h-12 rounded bg-surface-700 flex items-center justify-center overflow-hidden">
                        <Image size={20} className="text-surface-400" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded bg-surface-700 flex items-center justify-center">
                        <FileText size={20} className="text-red-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{doc.fileName}</p>
                      <p className="text-xs text-surface-500">
                        {(doc.fileSizeBytes / 1024).toFixed(0)} KB
                      </p>
                    </div>
                  </div>
                )}

                {/* Upload Button */}
                <input
                  ref={el => { fileInputRefs.current[config.type] = el; }}
                  type="file"
                  accept={config.accept}
                  capture={config.capture as any}
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file, config.type);
                    e.target.value = '';
                  }}
                />
                <button
                  onClick={() => fileInputRefs.current[config.type]?.click()}
                  disabled={isUploading}
                  className="w-full py-2 px-3 rounded-lg border border-dashed border-surface-600 hover:border-primary-500 text-surface-300 hover:text-primary-400 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {isUploading ? (
                    <><Loader2 size={16} className="animate-spin" /> Enviando...</>
                  ) : doc ? (
                    <><Upload size={16} /> Substituir</>
                  ) : (
                    <><Upload size={16} /> {config.capture ? 'Tirar Foto' : 'Enviar Arquivo'}</>
                  )}
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Submit */}
      <div className="flex justify-end pb-8">
        <Button variant="primary" size="lg" onClick={handleSubmit}
          disabled={!allRequiredUploaded || submitMutation.isPending}>
          {submitMutation.isPending ? (
            <><Loader2 size={20} className="mr-2 animate-spin" /> Enviando...</>
          ) : (
            <><Send size={20} className="mr-2" /> Enviar para Análise</>
          )}
        </Button>
      </div>
    </div>
  );
}
